import React, { useState } from "react";
import moment from "moment";
import { Icon } from "@iconify/react";
import { enqueueSnackbar } from "notistack";
import verifyBusiness from "../../../controllers/user/verifyBusiness";
import verifyBusinessRemove from "../../../controllers/user/verifyBusinessRemove";
import approveBusinessChangeRequest from "../../../controllers/user/approveBusinessChangeRequest";

const AGENCY_TYPES = {
  starterBusiness: "Starter business (not registered yet)",
  registeredBusinessWithIATALicense: "Registered business with IATA licence",
  registeredBusinessWithOutIATALicense:
    "Registered business without IATA licence",
};

const FIELD_LABELS = {
  agencyType: "Business type",
  tradingName: "Trading name",
  agencyName: "Agency name",
  businessEmail: "Business email",
  businessPhone: "Business phone",
  businessLocation: "Business address",
  registeredBusinessName: "Registered business name",
  typeOfBusiness: "Type of registered business",
  country: "Country",
  companyNumber: "RC number",
  taxIdentification: "TIN",
  firstName: "Contact first name",
  lastName: "Contact last name",
  position: "Job title",
  email: "Contact email",
  phoneNumber: "Contact phone",
  CACCertificateDoc: "CAC certificate",
  CACCO2Doc: "CAC status report (CO2)",
  CACC07Doc: "CAC particulars of directors (CO7)",
  identityVerificationDoc: "Director's ID (NIN or passport)",
  utilityBill: "Utility bill",
};

const DOCUMENTS = [
  ["CACCertificateDoc", "CAC certificate"],
  ["CACCO2Doc", "CAC status report (CO2)"],
  ["CACC07Doc", "CAC particulars of directors (CO7)"],
  ["identityVerificationDoc", "Director's ID (NIN or passport)"],
  ["utilityBill", "Utility bill"],
];

const PENDING_STATUSES = new Set([
  "pending",
  "requested",
  "open",
  "awaiting",
  "in_review",
  "in-review",
  "submitted",
]);

function text(v) {
  if (v == null || v === "") return null;
  return String(v);
}

function fieldLabel(field) {
  return (
    FIELD_LABELS[field] ||
    String(field || "Field")
      .replace(/([A-Z])/g, " $1")
      .trim()
  );
}

function isUrl(value) {
  const s = text(value);
  if (!s) return false;
  return /^https?:\/\//i.test(s) || /\.(pdf|png|jpe?g|webp|gif)(\?|$)/i.test(s);
}

function displayValue(value) {
  const s = text(value);
  if (!s) return null;
  if (AGENCY_TYPES[s]) return AGENCY_TYPES[s];
  return s;
}

function isPendingStatus(status) {
  return PENDING_STATUSES.has(String(status || "").toLowerCase());
}

function Row({ label, value }) {
  const shown = text(value);
  return (
    <div className="grid grid-cols-1 gap-0.5 border-b border-[#EEF1F6] py-2.5 last:border-b-0 sm:grid-cols-[minmax(120px,36%)_minmax(0,1fr)] sm:gap-4 sm:items-start">
      <dt className="text-sm text-[#5A6478]">{label}</dt>
      <dd
        className={`m-0 min-w-0 text-sm [overflow-wrap:anywhere] ${
          shown ? "font-medium text-[#111827]" : "font-semibold text-[#7A4A08]"
        }`}
      >
        {shown || "Missing"}
      </dd>
    </div>
  );
}

function Section({ title, description, children }) {
  return (
    <section className="overflow-hidden rounded-xl border border-[#E5EAF2] bg-white shadow-sm">
      <div className="border-b border-[#EEF1F6] px-4 py-3.5 sm:px-5">
        <h3 className="m-0 text-base font-semibold text-[#111827]">{title}</h3>
        {description ? (
          <p className="m-0 mt-0.5 text-sm text-[#5A6478]">{description}</p>
        ) : null}
      </div>
      <dl className="m-0 px-4 py-1 sm:px-5">{children}</dl>
    </section>
  );
}

function DocRow({ label, url }) {
  const href = text(url);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#EEF1F6] py-2.5 last:border-b-0">
      <div className="min-w-0">
        <p className="m-0 text-sm font-medium text-[#111827]">{label}</p>
        <p
          className={`m-0 mt-0.5 text-xs ${
            href ? "text-[#5A6478]" : "font-semibold text-[#7A4A08]"
          }`}
        >
          {href ? "Uploaded" : "Missing"}
        </p>
      </div>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-[#D6DEEA] bg-white px-2.5 text-xs font-semibold text-[#111827] hover:bg-[#F8FAFC]"
        >
          <Icon icon="mdi:open-in-new" width={14} height={14} aria-hidden="true" />
          Open
        </a>
      ) : null}
    </div>
  );
}

function ValueSide({ label, value, tone }) {
  const shown = displayValue(value);
  const href = isUrl(value) ? text(value) : null;
  const toneClass =
    tone === "before"
      ? "border-[#F0C2C4]/70 bg-[#FDF2F2]"
      : "border-emerald-200 bg-emerald-50/60";
  const labelClass = tone === "before" ? "text-[#A81E27]" : "text-emerald-800";

  return (
    <div className={`min-w-0 rounded-lg border px-3 py-2.5 ${toneClass}`}>
      <p
        className={`m-0 text-[11px] font-bold uppercase tracking-wide ${labelClass}`}
      >
        {label}
      </p>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#111827] underline-offset-2 hover:underline"
        >
          <Icon icon="mdi:file-document-outline" width={16} height={16} />
          Open document
          <Icon icon="mdi:open-in-new" width={14} height={14} />
        </a>
      ) : (
        <p
          className={`m-0 mt-1 text-sm [overflow-wrap:anywhere] ${
            shown ? "font-medium text-[#111827]" : "font-semibold text-[#7A4A08]"
          }`}
        >
          {shown || "Empty"}
        </p>
      )}
    </div>
  );
}

function ChangeDiffRow({ change }) {
  const from = change?.from;
  const to = change?.to;
  const same = String(from ?? "") === String(to ?? "");

  return (
    <div className="border-b border-[#F0D3A6]/60 px-4 py-3 last:border-b-0 sm:px-5">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <p className="m-0 text-sm font-semibold text-[#111827]">
          {fieldLabel(change?.field)}
        </p>
        {same ? (
          <span className="rounded-full bg-[#FBEED9] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#7A4A08]">
            Same value
          </span>
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <ValueSide label="Before" value={from} tone="before" />
        <ValueSide label="After" value={to} tone="after" />
      </div>
    </div>
  );
}

function ProfileReviewBody({ detail }) {
  const contact = detail.contact || {};
  const legal = detail.legalInfo || {};
  const address = detail.address || {};

  return (
    <div className="flex-1 space-y-3 overflow-y-auto scroll-hide px-3 py-3 sm:px-4 sm:py-4">
      <Section
        title="Business details"
        description="How the agency appears on bookings and invoices."
      >
        <Row
          label="Business type"
          value={AGENCY_TYPES[detail.agencyType] || detail.agencyType}
        />
        <Row
          label="Trading name"
          value={detail.tradingName || detail.agencyName}
        />
        <Row label="Business email" value={detail.businessEmail} />
        <Row label="Business phone" value={detail.businessPhone} />
        <Row label="Business address" value={address.businessLocation} />
      </Section>

      <Section
        title="Legal entity"
        description="Registered company details — should match CAC documents."
      >
        <Row
          label="Registered business name"
          value={detail.registeredBusinessName}
        />
        <Row label="Type of registered business" value={detail.typeOfBusiness} />
        <Row label="Country" value={legal.country} />
        <Row label="RC number" value={legal.companyNumber} />
        <Row label="TIN" value={legal.taxIdentification} />
      </Section>

      <Section title="Key contact" description="Primary person for this agency.">
        <Row
          label="Name"
          value={
            [contact.firstName, contact.lastName].filter(Boolean).join(" ") ||
            null
          }
        />
        <Row label="Job title" value={contact.position} />
        <Row label="Email" value={contact.email} />
        <Row label="Phone" value={contact.phoneNumber} />
      </Section>

      <section className="overflow-hidden rounded-xl border border-[#E5EAF2] bg-white shadow-sm">
        <div className="border-b border-[#EEF1F6] px-4 py-3.5 sm:px-5">
          <h3 className="m-0 text-base font-semibold text-[#111827]">
            Documents
          </h3>
          <p className="m-0 mt-0.5 text-sm text-[#5A6478]">
            Open each file and check it matches the legal details above.
          </p>
        </div>
        <div className="px-4 py-1 sm:px-5">
          {DOCUMENTS.map(([key, label]) => (
            <DocRow key={key} label={label} url={detail[key]} />
          ))}
        </div>
      </section>
    </div>
  );
}

/**
 * - Business-change request: show changes, approve/reject via change-request APIs
 * - First-time verification: show full profile, activate via verifyBusiness
 * - Otherwise: verify / remove on current profile
 */
export default function CustomerVerificationReviewPanel({
  data,
  changeRequest,
  changeRequestLoading,
  initialVerification,
  onReject,
  onApproved,
}) {
  const [loading, setLoading] = useState(false);
  const detail = data?.detail || {};
  const trading = detail.tradingName || detail.agencyName || "This agency";
  const requestId = changeRequest?._id || changeRequest?.id || "";
  const changes = Array.isArray(changeRequest?.changes)
    ? changeRequest.changes
    : [];
  const canActChange =
    Boolean(requestId && isPendingStatus(changeRequest?.status));
  const actionsDisabled =
    loading || changeRequestLoading || (changeRequest && !canActChange);

  const approveChange = async () => {
    if (!requestId) {
      enqueueSnackbar("Missing business change request id.", {
        variant: "error",
      });
      return;
    }
    setLoading(true);
    const res = await approveBusinessChangeRequest(data?._id, requestId);
    setLoading(false);
    if (!res?.return) {
      enqueueSnackbar(res?.msg || "Couldn't approve verification.", {
        variant: "error",
      });
      return;
    }
    enqueueSnackbar("Change request approved.", { variant: "success" });
    onApproved?.(res.data?.data || res.data);
  };

  const activateBusiness = async () => {
    setLoading(true);
    const res = await verifyBusiness(data?._id);
    setLoading(false);
    if (!res?.return) {
      enqueueSnackbar(res?.msg || "Couldn't activate business.", {
        variant: "error",
      });
      return;
    }
    enqueueSnackbar("Business activated.", { variant: "success" });
    onApproved?.(res.data?.data || res.data);
  };

  const removeVerification = async () => {
    setLoading(true);
    const res = await verifyBusinessRemove(data?._id);
    setLoading(false);
    if (!res?.return) {
      enqueueSnackbar(res?.msg || "Couldn't remove verification.", {
        variant: "error",
      });
      return;
    }
    enqueueSnackbar("Business verification removed.", { variant: "success" });
    onApproved?.(res.data?.data || res.data);
  };

  // First-time verification — no business-change request; use activate route
  if (initialVerification && !changeRequest) {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col bg-[#F7F9FC]">
        <div className="border-b border-[#F0D3A6] bg-[#FFF8EE] px-4 py-3 sm:px-5">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#FBEED9] text-[#B4690E]">
              <Icon
                icon="mdi:alert-circle-outline"
                width={20}
                height={20}
                aria-hidden="true"
              />
            </span>
            <div className="min-w-0">
              <p className="m-0 text-sm font-semibold text-[#7A4A08]">
                First-time verification request
              </p>
              <p className="m-0 mt-0.5 text-sm leading-snug text-[#7A4A08]/90">
                Review {trading}&apos;s profile and documents, then activate the
                business or reject with a reason.
              </p>
            </div>
          </div>
        </div>

        <ProfileReviewBody detail={detail} />

        <div
          className="flex flex-shrink-0 flex-wrap items-center gap-2 border-t border-[#E5EAF2] bg-white px-3 py-3 sm:px-4"
          style={{
            boxShadow:
              "0 -4px 6px -1px rgba(0,0,0,0.08), 0 -2px 4px -2px rgba(0,0,0,0.06)",
          }}
        >
          <button
            type="button"
            disabled={loading}
            onClick={activateBusiness}
            className="inline-flex h-11 min-w-[9rem] flex-1 items-center justify-center rounded-lg bg-primary1 px-4 text-sm font-semibold text-white disabled:opacity-60 sm:flex-none"
          >
            {loading ? <span className="buttonLoader" /> : "Activate business"}
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={onReject}
            className="inline-flex h-11 flex-1 items-center justify-center rounded-lg border border-[#F0C2C4] bg-white px-4 text-sm font-semibold text-[#A81E27] hover:bg-[#FDF2F2] disabled:opacity-60 sm:flex-none"
          >
            Reject request
          </button>
        </div>
      </div>
    );
  }

  // Business-change request (pending or history)
  if (changeRequest || changeRequestLoading) {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col bg-[#F7F9FC]">
        <div className="border-b border-[#F0D3A6] bg-[#FFF8EE] px-4 py-3 sm:px-5">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#FBEED9] text-[#B4690E]">
              <Icon
                icon="mdi:alert-circle-outline"
                width={20}
                height={20}
                aria-hidden="true"
              />
            </span>
            <div className="min-w-0">
              <p className="m-0 text-sm font-semibold text-[#7A4A08]">
                {canActChange
                  ? "Review business changes"
                  : "Change request detail"}
              </p>
              <p className="m-0 mt-0.5 text-sm leading-snug text-[#7A4A08]/90">
                {changeRequestLoading
                  ? "Loading request…"
                  : [
                      changeRequest?.section,
                      changeRequest?.status,
                      changes.length
                        ? `${changes.length} change${
                            changes.length === 1 ? "" : "s"
                          }`
                        : null,
                      changeRequest?.submittedAt
                        ? `submitted ${moment(
                            changeRequest.submittedAt,
                          ).format("DD MMM YYYY, HH:mm")}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scroll-hide px-3 py-3 sm:px-4 sm:py-4">
          {changeRequestLoading ? (
            <div className="rounded-xl border border-[#F0D3A6] bg-[#FFF8EE] px-4 py-8 text-center text-sm text-[#7A4A08]">
              Loading proposed changes…
            </div>
          ) : (
            <section className="overflow-hidden rounded-xl border border-[#F0D3A6] bg-white shadow-sm">
              <div className="border-b border-[#F0D3A6] bg-[#FFF8EE] px-4 py-3.5 sm:px-5">
                <h3 className="m-0 text-base font-semibold text-[#7A4A08]">
                  What changed
                  {changeRequest?.section ? ` · ${changeRequest.section}` : ""}
                </h3>
                <p className="m-0 mt-0.5 text-sm text-[#7A4A08]/90">
                  {canActChange
                    ? "Compare before → after for each field, then approve or reject."
                    : "Read-only view of this request."}
                </p>
              </div>
              {changes.length ? (
                changes.map((change) => (
                  <ChangeDiffRow
                    key={change?._id || `${change?.field}-${change?.to}`}
                    change={change}
                  />
                ))
              ) : (
                <p className="px-4 py-6 text-sm text-[#7A4A08] sm:px-5">
                  This request has no field-level changes listed.
                </p>
              )}
            </section>
          )}
        </div>

        <div
          className="flex flex-shrink-0 flex-wrap items-center gap-2 border-t border-[#E5EAF2] bg-white px-3 py-3 sm:px-4"
          style={{
            boxShadow:
              "0 -4px 6px -1px rgba(0,0,0,0.08), 0 -2px 4px -2px rgba(0,0,0,0.06)",
          }}
        >
          {canActChange ? (
            <>
              <button
                type="button"
                disabled={actionsDisabled}
                onClick={approveChange}
                className="inline-flex h-11 min-w-[9rem] flex-1 items-center justify-center rounded-lg bg-primary1 px-4 text-sm font-semibold text-white disabled:opacity-60 sm:flex-none"
              >
                {loading ? (
                  <span className="buttonLoader" />
                ) : (
                  "Approve changes"
                )}
              </button>
              <button
                type="button"
                disabled={actionsDisabled}
                onClick={onReject}
                className="inline-flex h-11 flex-1 items-center justify-center rounded-lg border border-[#F0C2C4] bg-white px-4 text-sm font-semibold text-[#A81E27] hover:bg-[#FDF2F2] disabled:opacity-60 sm:flex-none"
              >
                Reject request
              </button>
            </>
          ) : (
            <p className="m-0 text-sm text-[#6D7C94]">
              This request is {changeRequest?.status || "closed"} — approve /
              reject are unavailable.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Manual verify / remove (no pending request)
  return (
    <div className="flex h-full min-h-0 flex-1 flex-col bg-[#F7F9FC]">
      <div className="border-b border-[#EEF1F6] bg-white px-4 py-3 text-sm text-[#5A6478] sm:px-5">
        Business verification for {trading}.
      </div>
      <ProfileReviewBody detail={detail} />
      <div
        className="flex flex-shrink-0 flex-wrap items-center gap-2 border-t border-[#E5EAF2] bg-white px-3 py-3 sm:px-4"
        style={{
          boxShadow:
            "0 -4px 6px -1px rgba(0,0,0,0.08), 0 -2px 4px -2px rgba(0,0,0,0.06)",
        }}
      >
        {detail.isVerified ? (
          <button
            type="button"
            disabled={loading}
            onClick={removeVerification}
            className="inline-flex h-11 items-center justify-center rounded-lg bg-[#B81F25] px-4 text-sm font-semibold text-white disabled:opacity-60"
          >
            {loading ? <span className="buttonLoader" /> : "Remove verification"}
          </button>
        ) : (
          <button
            type="button"
            disabled={loading}
            onClick={activateBusiness}
            className="inline-flex h-11 min-w-[9rem] items-center justify-center rounded-lg bg-primary1 px-4 text-sm font-semibold text-white disabled:opacity-60"
          >
            {loading ? <span className="buttonLoader" /> : "Activate business"}
          </button>
        )}
      </div>
    </div>
  );
}
